import { ICheckInLog, IUser } from "@vierkant-software/types__api";
import { FullSyncableServiceBase } from "./sync.service";
import { computed, effect, inject, Injectable, Resource, signal } from "@angular/core";
import { DateTime } from "luxon";
import { UserSyncService } from "./userSync.service";
import { environment } from "src/environments/environment";

export interface AttendanceLogEntry {
    /** ID of the underlying check-in log - shared by the check-in and its check-out */
    ID: string;
    user: Resource<IUser>;
    at: DateTime;
    type: AttendanceType;
    /** current area of the member */
    area_ID?: string;
}

export enum CheckInDetailLevel {
    default = 0,
    attendance = 127,
}

type CheckInDetailType = {
    [CheckInDetailLevel.default]: Partial<ICheckInLog>;
    [CheckInDetailLevel.attendance]: Partial<ICheckInLog>;
};

type AttendanceData = {
    ID: string;
    at: DateTime;
    user_ID: string;
    area_ID?: string;
};

export enum AttendanceType {
    checkin = 'checkin',
    checkout = 'checkout',
}

@Injectable({ providedIn: "root" })
export class CheckInSyncService extends FullSyncableServiceBase <CheckInDetailType, CheckInDetailLevel> {
    private userSyncService = inject(UserSyncService);
    private checkInResources = signal<Resource<ICheckInLog>[]>([]);
    private checkInData = signal<AttendanceData[]>([]);
    private checkOutData = signal<AttendanceData[]>([]);
    private attendanceLog = computed(() => {
        const checkIns = this.checkInData().map(checkIn => ({
            ID:      checkIn.ID,
            user:    this.userSyncService.get(checkIn.user_ID),
            at:      checkIn.at,
            type:    AttendanceType.checkin,
            area_ID: checkIn.area_ID,
        }));

        const checkOuts = this.checkOutData().map(checkOut => ({
            ID:      checkOut.ID,
            user:    this.userSyncService.get(checkOut.user_ID),
            at:      checkOut.at,
            type:    AttendanceType.checkout,
            area_ID: checkOut.area_ID,
        }));

        const combined: AttendanceLogEntry[] = [...checkIns, ...checkOuts];

        return combined.sort((a, b) =>
            (b.at?.toMillis() ?? 0) - (a.at?.toMillis() ?? 0)
        );
    });
    private _checkInLog = computed(() => this.attendanceLog().filter(entry => entry.type === AttendanceType.checkin));
    private _checkOutLog = computed(() => this.attendanceLog().filter(entry => entry.type === AttendanceType.checkout));

    get checkInLog() {
        return this._checkInLog();
    }

    get checkOutLog() {
        return this._checkOutLog();
    }

    get combinedLog() {
        return this.attendanceLog();
    }

    constructor() {
        super();

        effect(() => {
            const resources = this.checkInResources();

            for (const resource of resources) {
                const value = resource.value();

                if (!value?.ID)
                    continue;

                this.checkInData.update((prev) => {
                    const filtered = prev.filter(c => c.ID !== value.ID);
                    const checkInData: AttendanceData = {
                        ID:      value.ID,
                        at:      value.checkinDate,
                        user_ID: value.user_ID,
                        area_ID: value.CurrentCheckinArea_ID,
                    };
                    return [...filtered, checkInData];
                });
            }
        });

        this.dataChange$.subscribe((data) => {
            if (data.type === "evicted") {
                for (const checkIn of data.items) {
                    this.checkInResources.update(prev => {
                        return prev.filter(resource => {
                            const value = resource.value();
                            return value?.ID !== checkIn.ID;
                        });
                    });
                    const checkInForCheckOut = this.checkInData().find(c => c.ID === checkIn.ID);

                    if (!checkInForCheckOut) {
                        console.warn(`No check-in data found for evicted check-in ID: ${checkIn.ID}`);
                        continue;
                    }

                    const checkOutData: AttendanceData = {
                        ID:      checkIn.ID,
                        at:      DateTime.serverTime(),
                        user_ID: checkInForCheckOut.user_ID,
                        area_ID: checkInForCheckOut.area_ID,
                    };

                    this.checkOutData.update((prev) => {
                        const filtered = prev.filter(c => c.ID !== checkIn.ID);
                        return [...filtered, checkOutData];
                    });
                }
                return;
            }

            if (data.type === "loaded" || data.type === "injected") {
                for (const checkIn of data.items) {
                    const checkInResource = this.get(checkIn.ID, CheckInDetailLevel.attendance);

                    if (!checkInResource)
                        continue;

                    this.checkInResources.update((prev) => {
                        const filtered = prev.filter(resource => {
                            const value = resource.value();
                            return value?.ID !== checkIn.ID;
                        });
                        return [...filtered, checkInResource];
                    });
                }
            }
        });
    }

    override getDefaultValue(): CheckInDetailType[CheckInDetailLevel] {
        return {} as CheckInDetailType[CheckInDetailLevel];
    }
    protected readonly syncName = "CheckIn";
    protected override get usesDeviceSession(): boolean {
        return environment.isCashRegister;
    }
}